import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { AlertCircle, Printer, Loader2 } from "lucide-react";
import { releaseImpound, earlyReleaseImpound, quoteImpound, fetchImpoundConfig } from "../lib/api";
import { toast } from "sonner";
import PrintReceipt from "./PrintReceipt";
import PrintReleaseFormButton from "./PrintReleaseFormButton";
import { tenantLocale, tenantTz } from "../lib/company";
import { overstayDays } from "../lib/tenantTime";

const fmtMoney = (n) =>
  n == null ? "—" : `$${Number(n).toLocaleString(tenantLocale(), { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (iso) =>
  iso ? new Date(iso).toLocaleString(tenantLocale(), { timeZone: tenantTz(), dateStyle: "medium", timeStyle: "short", hourCycle: "h23" }) : "—";

/**
 * Days-held for early-release, computed in NZ calendar-days to match the
 * backend's `_days_between` helper (see /app/backend/impounds.py) — bug
 * fix Feb 2026.
 *
 * The OLD implementation used `Math.floor(elapsedMs / 86400000) + 1`,
 * which counts complete 24-hour blocks rather than crossings of the NZ
 * date boundary. Whenever the release time-of-day was *earlier* than
 * the impound time-of-day, the frontend under-counted by one day —
 * dialog showed "2 days · $48.60" while the backend saved a
 * pricing_breakdown of 3 days · $72.90, leaving the paid amount and
 * the recorded total off by exactly one day's storage.
 *
 * Correct approach: convert both timestamps to their NZ calendar DATES
 * (via `Intl.DateTimeFormat` with `timeZone: tenantTz()`),
 * subtract, add 1 for inclusivity, floor at 1 for same-day releases.
 * This produces the same integer the backend produces every time.
 */
const nzDateFmt = () => new Intl.DateTimeFormat("en-CA", {
  timeZone: tenantTz(),
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});
function _nzDateOnly(dateOrIso) {
  const d = dateOrIso instanceof Date ? dateOrIso : new Date(dateOrIso);
  // "en-CA" locale outputs "YYYY-MM-DD" which parses cleanly as UTC
  // midnight, so date-only arithmetic below stays timezone-safe.
  return new Date(`${nzDateFmt().format(d)}T00:00:00Z`);
}
function nzDaysHeldInclusive(impoundedIso, releasedAt = new Date()) {
  const start = _nzDateOnly(impoundedIso || releasedAt);
  const end = _nzDateOnly(releasedAt);
  const days = Math.round((end.getTime() - start.getTime()) / 86400000) + 1;
  return Math.max(1, days);
}

const PAYMENT_METHODS = [
  { value: "cash", label: "Cash" },
  { value: "eftpos", label: "EFTPOS" },
  { value: "credit_card", label: "Credit Card" },
  { value: "bank_transfer", label: "Bank Transfer" },
  { value: "other", label: "Other" },
];

/**
 * Release & take-payment flow:
 *   1. Re-quote on open (captures any overstay accrued since impound).
 *   2. Dispatcher enters payment_method + actual amount taken.
 *   3. Two-step confirm (anti-mis-click).
 *   4. POST release → backend snapshots quote + marks completed.
 *   5. window.print() — the print-only template renders 2 stacked copies
 *      (Customer + Office) on one A4 page with a "cut here" divider.
 */
export default function ReleaseImpoundDialog({ job, open, onOpenChange, onReleased, mode = "normal" }) {
  const isEarly = mode === "early";
  const [quote, setQuote] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState("eftpos");
  const [paymentAmount, setPaymentAmount] = useState("");
  // What we're actually charging. Starts as the quote; the operator can
  // reduce it (weekend storage we don't charge, a settled round number) and
  // then the invoice, the receipt and the billing state all follow it.
  const [chargeAmount, setChargeAmount] = useState("");
  const [chargeReason, setChargeReason] = useState("");
  const [releaseNotes, setReleaseNotes] = useState("");
  const [authorisedBy, setAuthorisedBy] = useState("");
  const [dailyRate, setDailyRate] = useState(null);
  const [daysHeld, setDaysHeld] = useState(1);
  const [confirming, setConfirming] = useState(false);
  const [saving, setSaving] = useState(false);
  const [receipt, setReceipt] = useState(null); // saved job after release — drives the printable

  // Reset + re-quote on open.
  useEffect(() => {
    if (!open || !job) return;
    setConfirming(false);
    setSaving(false);
    setReceipt(null);
    setReleaseNotes("");
    setPaymentMethod("eftpos");
    setAuthorisedBy("");
    setChargeReason("");
    let alive = true;
    (async () => {
      try {
        if (isEarly) {
          // Police-authorised early release. Pull the impound config so we
          // know the correct daily rate for the vehicle's weight class,
          // then compute storage as (daily × days actually held). Tow fee
          // + additional km fee are carried forward from the existing
          // snapshot untouched.
          const cfg = await fetchImpoundConfig();
          const gvm = Number(job.gvm_kg || 0);
          const lightThreshold = Number(cfg.light_max_gvm_kg || 3500);
          const isLight = gvm <= lightThreshold;
          const rate = Number(
            isLight ? cfg.overstay_per_day_light : cfg.overstay_per_day_heavy
          ) || 0;
          // Days held — computed in NZ calendar-days to match the backend
          // (see `nzDaysHeldInclusive` above for the bug fix rationale).
          const days = nzDaysHeldInclusive(job.impounded_at);
          if (!alive) return;
          setDailyRate(rate);
          setDaysHeld(days);
          const prior = job.pricing_breakdown?.items || job.pricing_breakdown?.line_items || {};
          const tow = Number(prior.tow_fee || 0);
          const km = Number(prior.additional_km_fee || 0);
          const storage = +(rate * days).toFixed(2);
          const total = +(tow + km + storage).toFixed(2);
          setQuote({
            line_items: {
              tow_fee: tow,
              additional_km_fee: km,
              impound_fee: storage,
              overstay_fee: 0,
            },
            total,
          });
          const paid = (job.part_payments || []).reduce((a, p) => a + (Number(p.amount) || 0), 0);
          setChargeAmount(total.toFixed(2));
          setPaymentAmount(String(Math.max(0, +(total - paid).toFixed(2))));
          return;
        }
        // Normal release: calendar days past release_due_at (any partial day counts as +1, never negative).
        const overstay = overstayDays(job.impound_release_due_at);
        const q = await quoteImpound({
          gvm_kg: Number(job.gvm_kg || 0),
          impounded_at: job.impounded_at,
          term: job.impound_term || "28d",
          base_to_base_km: job.route_distance_m ? job.route_distance_m / 1000 : null,
          days_overstay: overstay,
          is_public_holiday: job.is_public_holiday || null,
        });
        if (alive) {
          setQuote(q);
          const paid = (job.part_payments || []).reduce((a, p) => a + (Number(p.amount) || 0), 0);
          setChargeAmount(Number(q.total || 0).toFixed(2));
          setPaymentAmount(String(Math.max(0, +(q.total - paid).toFixed(2))));
        }
      } catch (e) {
        if (alive) toast.error("Couldn't re-quote — using saved breakdown");
      }
    })();
    return () => { alive = false; };
  }, [open, job, isEarly]);

  const breakdown = quote || job?.pricing_breakdown;
  const partPaid = (job?.part_payments || []).reduce((a, p) => a + (Number(p.amount) || 0), 0);
  const km = job?.route_distance_m ? job.route_distance_m / 1000 : null;
  // Count the days directly — this used to divide the fee by hard-coded
  // $24.30 / $56.70 rates, which shows the wrong number of days the moment a
  // tenant edits their rates or runs on Australian ones.
  const overstayDayCount = overstayDays(job?.impound_release_due_at);

  // Charge vs quote. A blank box means "charge the quote".
  const quotedTotal = Number(breakdown?.total || 0);
  const charging = chargeAmount.trim() === "" ? quotedTotal : Number(chargeAmount);
  const chargeValid = Number.isFinite(charging) && charging >= 0;
  const adjustment = chargeValid ? +(charging - quotedTotal).toFixed(2) : 0;
  const isAdjusted = Math.abs(adjustment) >= 0.01;
  const balanceDue = Math.max(0, +(charging - partPaid).toFixed(2));

  // Keep the money taken in step with the charge until the operator types
  // their own figure — the common case is "charge this, take this".
  useEffect(() => {
    if (!open || !chargeValid) return;
    setPaymentAmount(balanceDue.toFixed(2));
    // Intentionally keyed on the charge only: re-running on paymentAmount
    // would fight the operator typing a part payment.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chargeAmount, open]);

  const onConfirmRelease = async () => {
    if (!paymentMethod || !paymentAmount) {
      toast.error("Payment method and amount required");
      return;
    }
    if (!chargeValid) {
      toast.error("Enter a valid charge");
      return;
    }
    if (isAdjusted && !chargeReason.trim()) {
      toast.error("Say why the charge differs from the quote");
      return;
    }
    if (isEarly && !authorisedBy.trim()) {
      toast.error("Enter who authorised the early release (e.g. officer name)");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        payment_method: paymentMethod,
        payment_amount: Number(paymentAmount),
        base_to_base_km: km,
        release_notes: releaseNotes || "",
        ...(isAdjusted
          ? { charge_override: charging, charge_override_reason: chargeReason.trim() }
          : {}),
      };
      const updated = isEarly
        ? await earlyReleaseImpound(job.id, { ...payload, authorised_by: authorisedBy.trim() })
        : await releaseImpound(job.id, payload);
      setReceipt(updated);
      toast.success(isEarly ? "Early release recorded — printing receipts" : "Released — printing receipts");
      setTimeout(() => {
        window.print();
      }, 250);
      onReleased?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Release failed");
    } finally {
      setSaving(false);
    }
  };

  if (!job) return null;

  return (
    <>
      <Dialog open={open && !receipt} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-lg" data-testid="release-impound-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl font-black tracking-tight">
              {isEarly ? "Early Release · Police Authorised" : "Release & Take Payment"}
            </DialogTitle>
            <DialogDescription>
              {job.rego || "—"} · {job.vehicle} · {job.impound_owner_name || job.customer_name}
              {isEarly && (
                <span className="block mt-1 text-amber-700 dark:text-amber-400 text-[11px] font-medium">
                  Storage prorated to {daysHeld} day{daysHeld !== 1 ? "s" : ""} @ ${dailyRate?.toFixed?.(2) ?? "0.00"}/day.
                  Tow &amp; km fees unchanged.
                </span>
              )}
            </DialogDescription>
          </DialogHeader>

          {/* Live breakdown */}
          {breakdown && (
            <div className="rounded-md border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/30 p-3 my-2" data-testid="release-quote">
              <div className="text-[10px] uppercase tracking-wider font-bold text-blue-700 dark:text-blue-300 mb-2">Final Quote</div>
              <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                <div className="text-slate-600 dark:text-slate-300">Tow Fee</div><div className="text-right font-mono">{fmtMoney(breakdown.line_items.tow_fee)}</div>
                <div className="text-slate-600 dark:text-slate-300">
                  {isEarly
                    ? `Storage (${daysHeld} day${daysHeld !== 1 ? "s" : ""} @ ${fmtMoney(dailyRate)})`
                    : "Impound Fee"}
                </div><div className="text-right font-mono">{fmtMoney(breakdown.line_items.impound_fee)}</div>
                <div className="text-slate-600 dark:text-slate-300">Additional Km{km ? ` (${km.toFixed(1)}km)` : ""}</div><div className="text-right font-mono">{fmtMoney(breakdown.line_items.additional_km_fee)}</div>
                {!isEarly && (
                  <>
                    <div className="text-slate-600 dark:text-slate-300">Overstay ({overstayDayCount} day{overstayDayCount === 1 ? "" : "s"})</div><div className="text-right font-mono">{fmtMoney(breakdown.line_items.overstay_fee)}</div>
                  </>
                )}
              </div>
              <div className="mt-2 pt-2 border-t border-blue-200 dark:border-blue-800 flex justify-between items-center">
                <div className="text-xs font-bold text-slate-700 dark:text-slate-200">QUOTED</div>
                <div className="font-mono font-black text-lg text-blue-900 dark:text-blue-100">{fmtMoney(quotedTotal)}</div>
              </div>

              {/* The charge is a decision, not the arithmetic. Before this the
                  operator could only edit the payment, so taking $50 on a
                  $187.40 quote left $137.40 on the invoicing list. */}
              <div className="mt-2 flex items-center justify-between gap-2">
                <Label className="uppercase-label mb-0">Charging</Label>
                <div className="relative w-32">
                  <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-slate-500">$</span>
                  <Input
                    type="number" step="0.01" min="0"
                    className="pl-5 font-mono h-8 text-right"
                    value={chargeAmount}
                    onChange={(e) => setChargeAmount(e.target.value)}
                    data-testid="release-charge-amount"
                  />
                </div>
              </div>
              {isAdjusted && (
                <div className="mt-1.5 space-y-1.5" data-testid="release-adjustment">
                  <div className="flex justify-between text-xs font-semibold text-amber-800 dark:text-amber-300">
                    <span>Adjustment</span>
                    <span className="font-mono">{adjustment < 0 ? "−" : "+"}{fmtMoney(Math.abs(adjustment))}</span>
                  </div>
                  <Input
                    placeholder="Why? e.g. weekend storage not charged"
                    value={chargeReason}
                    onChange={(e) => setChargeReason(e.target.value)}
                    className="h-8 text-xs"
                    data-testid="release-charge-reason"
                  />
                  <p className="text-[10px] text-slate-500 dark:text-slate-400 italic">
                    Goes on the receipt and the invoice as its own line, so nothing
                    is left owing afterwards.
                  </p>
                </div>
              )}
              {partPaid > 0 && (
                <>
                  <div className="mt-1 flex justify-between text-xs text-emerald-800 dark:text-emerald-300">
                    <span>Part payments received ({(job.part_payments || []).length})</span>
                    <span className="font-mono font-semibold">−{fmtMoney(partPaid)}</span>
                  </div>
                </>
              )}
              <div className="mt-1 pt-1 border-t border-blue-200 dark:border-blue-800 flex justify-between items-center">
                <div className="text-xs font-bold text-emerald-800 dark:text-emerald-200">BALANCE DUE</div>
                <div className="font-mono font-black text-lg text-emerald-800 dark:text-emerald-200" data-testid="release-balance-due">
                  {fmtMoney(balanceDue)}
                </div>
              </div>
            </div>
          )}

          {/* Payment capture */}
          <div className="grid grid-cols-2 gap-3 mt-2">
            {isEarly && (
              <div className="col-span-2">
                <Label className="uppercase-label">Authorised By *</Label>
                <Input
                  placeholder="e.g. Constable Smith, Central Police"
                  value={authorisedBy}
                  onChange={(e) => setAuthorisedBy(e.target.value)}
                  data-testid="release-authorised-by"
                />
              </div>
            )}
            <div>
              <Label className="uppercase-label">Payment Method *</Label>
              <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                <SelectTrigger data-testid="release-payment-method"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PAYMENT_METHODS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="uppercase-label">Amount Taken *</Label>
              <div className="relative">
                <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-slate-500">$</span>
                <Input type="number" step="0.01" min="0" className="pl-5 font-mono" value={paymentAmount} onChange={(e) => setPaymentAmount(e.target.value)} data-testid="release-payment-amount"/>
              </div>
            </div>
            <div className="col-span-2">
              <Label className="uppercase-label">Release Notes (optional)</Label>
              <Textarea rows={2} value={releaseNotes} onChange={(e) => setReleaseNotes(e.target.value)} placeholder="e.g. partial payment plan, contested by owner, etc." data-testid="release-notes"/>
            </div>
          </div>

          {/* Step 1 → Step 2 confirm */}
          {confirming && (
            <div className="mt-3 rounded-md border-2 border-rose-300 dark:border-rose-800 bg-rose-50 dark:bg-rose-950/30 p-3 flex items-start gap-2" data-testid="release-confirm-banner">
              <AlertCircle className="h-5 w-5 text-rose-600 shrink-0 mt-0.5"/>
              <div className="text-sm text-rose-900 dark:text-rose-200">
                <div className="font-bold">Confirm release?</div>
                <div className="text-xs mt-0.5">This will mark the vehicle as released, record payment of <strong>{fmtMoney(Number(paymentAmount))}</strong> via <strong>{PAYMENT_METHODS.find(p => p.value === paymentMethod)?.label}</strong>, and auto-print 2 receipts. Cannot be undone here (needs an admin edit).</div>
                {isAdjusted && (
                  <div className="text-xs mt-1 font-semibold" data-testid="release-confirm-adjustment">
                    Charging {fmtMoney(charging)} instead of the quoted {fmtMoney(quotedTotal)} — {chargeReason.trim() || "no reason given"}.
                  </div>
                )}
              </div>
            </div>
          )}

          <DialogFooter className="mt-2 flex-wrap gap-2">
            {!confirming ? (
              <>
                <PrintReleaseFormButton
                  jobId={job?.id}
                  action="vehicle_to_owner"
                  label="Print Release Form"
                  testId="release-print-form"
                  className="mr-auto"
                />
                <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="release-cancel">Cancel</Button>
                <Button onClick={() => setConfirming(true)} className={isEarly ? "bg-slate-900 hover:bg-slate-800 text-white border border-slate-800 dark:bg-transparent dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:border-slate-500 dark:hover:text-white" : "bg-emerald-600 hover:bg-emerald-700 text-white"} data-testid="release-step1">
                  {isEarly ? "Early Release & Take Payment" : "Release & Take Payment"}
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={() => setConfirming(false)} data-testid="release-back">Back</Button>
                <Button onClick={onConfirmRelease} disabled={saving} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="release-confirm-final">
                  {saving ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin"/> : <Printer className="h-3.5 w-3.5 mr-1.5"/>}
                  Confirm & Print
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Post-release confirmation dialog */}
      {receipt && (
        <Dialog open={open} onOpenChange={(o) => { if (!o) { setReceipt(null); onOpenChange(false); }}}>
          <DialogContent className="max-w-md" data-testid="release-done-dialog">
            <DialogHeader>
              <DialogTitle className="text-emerald-700 dark:text-emerald-300">✓ Released</DialogTitle>
              <DialogDescription>If the print dialog didn&apos;t appear, click below to reopen it.</DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => window.print()} data-testid="release-reprint"><Printer className="h-3.5 w-3.5 mr-1.5"/>Re-print</Button>
              <Button onClick={() => { setReceipt(null); onOpenChange(false); }} className="bg-slate-900 hover:bg-slate-800 text-white border border-slate-800 dark:bg-transparent dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:border-slate-500 dark:hover:text-white" data-testid="release-done">Done</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Print-only template — 2 separate A4 sheets (Customer + Office) */}
      {receipt && <PrintReceipt job={receipt} mode="paid" />}
    </>
  );
}
